import { z } from "zod";

import { type SubmitSection } from "@/src/app/api/tasks/[taskId]/advance-intake/route";
import { type TaskHistoryResponse } from "@/src/app/api/tasks/[taskId]/history/route";
import { PatientLookUpStep } from "@/src/lib/constants";
import {
  confirmInsuranceNetworkResponseSchema,
  confirmInsuranceNetworkRequestSchema,
  type ConfirmInsuranceNetworkRequest,
} from "@/src/models/provider-insurance-requirements";
import { type ReferringProviderEhrEmailSendResult } from "@/src/models/referring-provider-ehr-email";
import {
  type SchedulingRecommendation,
  type VerifySchedulingRecommendationInput,
} from "@/src/models/scheduling-recommendation";
import {
  type StepCardActionRequest,
  type StepCardActionResponse,
  stepCardActionResponseSchema,
} from "@/src/models/step-card-action";
import { type StepCardsResponse, stepCardsResponseSchema } from "@/src/models/step-card-view";
import { type ChildTaskSummary, type Task } from "@/src/models/task";
import { type TaskApproval } from "@/src/models/task-approval";
import {
  type TaskFacetCounts,
  type TaskFacetCountsParams,
  type TaskFiltersParams,
  type PaginatedTaskFiltersParams,
} from "@/src/models/task-filters";
import { type TaskMetadata } from "@/src/models/task-metadata";
import {
  type TaskStepVerification,
  type UpsertTaskStepVerification,
  taskStepVerificationListSchema,
  taskStepVerificationSchema,
} from "@/src/models/task-step-verification";
import { VerificationResult } from "@/src/models/task-verification";
import {
  manualStepContinuationResponseSchema,
  workflowStepResumeCheckSchema,
  workflowStepResumeRequestSchema,
  workflowStepResumeResponseSchema,
  type WorkflowStepResumeRequest,
} from "@/src/models/workflow-step-resume";
import { fetchJSON, isNotFound } from "@/src/requests/utils/fetch-json";

export async function getTaskSchedulingRecommendation(taskId: string): Promise<SchedulingRecommendation | null> {
  return fetchJSON<SchedulingRecommendation | null>(`/api/tasks/${taskId}/scheduling-recommendation`);
}

export async function regenerateTaskSchedulingRecommendation(taskId: string): Promise<SchedulingRecommendation> {
  return fetchJSON<SchedulingRecommendation>(`/api/tasks/${taskId}/scheduling-recommendation`, {
    method: "POST",
  });
}

// Records the correct/incorrect verification on the linked referral's scheduling recommendation and
// completes the Scheduling Eligibility Verification task in a single call.
export async function verifySchedulingEligibilityTask(
  taskId: string,
  payload: VerifySchedulingRecommendationInput
): Promise<Task> {
  return fetchJSON<Task>(`/api/tasks/${taskId}/verify-scheduling-eligibility`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getTasksMetadata(
  { showCompleted = false, status, type, assignedToUser, priority, sortBy, sortOrder }: TaskFiltersParams = {
    showCompleted: false,
    sortOrder: "desc",
  }
): Promise<Task[]> {
  const searchParams = new URLSearchParams({
    showCompleted: showCompleted.toString(),
  });

  if (status && status.length > 0) {
    searchParams.set("status", status.join(","));
  }

  if (type && type.length > 0) {
    searchParams.set("type", type.join(","));
  }

  if (assignedToUser && assignedToUser.length > 0) {
    searchParams.set("assignedToUser", assignedToUser.join(","));
  }

  if (priority && priority.length > 0) {
    searchParams.set("priority", priority.join(","));
  }

  if (sortBy) {
    searchParams.set("sortBy", sortBy);
  }

  if (sortOrder) {
    searchParams.set("sortOrder", sortOrder);
  }

  return fetchJSON<Task[]>(`/api/tasks/metadata?${searchParams.toString()}`);
}

interface GetAllTasksParams {
  limit: number;
  lastUpdated?: string;
  showCompleted: boolean;
}
type GetAllTasksResponse = Record<string, Task>;

export async function getAllTasks({
  limit,
  lastUpdated,
  showCompleted,
}: GetAllTasksParams): Promise<GetAllTasksResponse> {
  const searchParams = new URLSearchParams({
    limit: limit.toString(),
    showCompleted: showCompleted.toString(),
  });

  if (lastUpdated) {
    searchParams.set("updatedSince", lastUpdated);
  }

  return fetchJSON<GetAllTasksResponse>(`/api/tasks?${searchParams.toString()}`);
}

export async function getTaskById(taskId: string): Promise<Task | null> {
  return fetchJSON<Task | null>(`/api/tasks/${taskId}`);
}

export async function getChildTasks(taskId: string): Promise<ChildTaskSummary[]> {
  return fetchJSON<ChildTaskSummary[]>(`/api/tasks/${taskId}/children`);
}

export async function sendReferringProviderEmail(taskId: string): Promise<ReferringProviderEhrEmailSendResult> {
  return fetchJSON<ReferringProviderEhrEmailSendResult>(`/api/tasks/${taskId}/send-referring-provider-email`, {
    method: "POST",
  });
}

export async function getAllPatientTasks(patientId: string): Promise<GetAllTasksResponse> {
  return fetchJSON<GetAllTasksResponse>(`/api/patients/${patientId}/tasks`);
}

export async function getAllFileTasks(fileId: string): Promise<GetAllTasksResponse> {
  return fetchJSON<GetAllTasksResponse>(`/api/files/${fileId}/tasks`);
}

export interface PaginatedTasksMetadataResponse {
  tasks: Record<string, TaskMetadata>;
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

export async function getDocumentClassificationFilterSlugs(): Promise<string[]> {
  const { slugs } = await fetchJSON<{ slugs: string[] }>("/api/tasks/classification-filter-slugs");

  return slugs;
}

export async function getPaginatedTasksMetadata(
  {
    page = 0,
    pageSize = 30,
    showCompleted = false,
    status,
    type,
    assignedToUser,
    priority,
    tags,
    documentClassification,
    source,
    authorizationNumber,
    sortBy,
    sortOrder,
    query,
    filterOutsideSla = false,
    patientId,
    patientGivenName,
    patientFamilyName,
  }: PaginatedTaskFiltersParams = {
    page: 0,
    pageSize: 30,
    showCompleted: false,
    sortOrder: "desc",
    filterOutsideSla: false,
  }
): Promise<PaginatedTasksMetadataResponse> {
  const searchParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    showCompleted: showCompleted.toString(),
    filterOutsideSla: filterOutsideSla.toString(),
  });

  if (patientId) {
    searchParams.set("patientId", patientId);
  }

  if (patientGivenName) {
    searchParams.set("patientGivenName", patientGivenName);
  }

  if (patientFamilyName) {
    searchParams.set("patientFamilyName", patientFamilyName);
  }

  if (status && status.length > 0) {
    searchParams.set("status", status.join(","));
  }

  if (type && type.length > 0) {
    searchParams.set("type", type.join(","));
  }

  if (assignedToUser && assignedToUser.length > 0) {
    searchParams.set("assignedToUser", assignedToUser.join(","));
  }

  if (priority && priority.length > 0) {
    searchParams.set("priority", priority.join(","));
  }

  if (tags && tags.length > 0) {
    searchParams.set("tags", tags.join(","));
  }

  if (documentClassification && documentClassification.length > 0) {
    searchParams.set("documentClassification", documentClassification.join(","));
  }

  if (source && source.length > 0) {
    searchParams.set("source", source.join(","));
  }

  if (authorizationNumber) {
    searchParams.set("authorizationNumber", authorizationNumber);
  }

  if (sortBy) {
    searchParams.set("sortBy", sortBy);
  }

  if (sortOrder) {
    searchParams.set("sortOrder", sortOrder);
  }

  if (query) {
    searchParams.set("query", query);
  }

  return fetchJSON<PaginatedTasksMetadataResponse>(`/api/tasks/metadata/paginated?${searchParams.toString()}`);
}

export async function getTaskFacetCounts(
  {
    showCompleted = false,
    filterOutsideSla = false,
    status,
    type,
    assignedToUser,
    priority,
    tags,
    documentClassification,
    source,
    query,
    facets,
  }: TaskFacetCountsParams = {
    showCompleted: false,
    filterOutsideSla: false,
  }
): Promise<TaskFacetCounts> {
  const searchParams = new URLSearchParams({
    showCompleted: showCompleted.toString(),
    filterOutsideSla: filterOutsideSla.toString(),
  });

  if (facets && facets.length > 0) {
    searchParams.set("facets", facets.join(","));
  }

  if (status && status.length > 0) {
    searchParams.set("status", status.join(","));
  }

  if (type && type.length > 0) {
    searchParams.set("type", type.join(","));
  }

  if (assignedToUser && assignedToUser.length > 0) {
    searchParams.set("assignedToUser", assignedToUser.join(","));
  }

  if (priority && priority.length > 0) {
    searchParams.set("priority", priority.join(","));
  }

  if (tags && tags.length > 0) {
    searchParams.set("tags", tags.join(","));
  }

  if (documentClassification && documentClassification.length > 0) {
    searchParams.set("documentClassification", documentClassification.join(","));
  }

  if (source && source.length > 0) {
    searchParams.set("source", source.join(","));
  }

  if (query) {
    searchParams.set("query", query);
  }

  return fetchJSON<TaskFacetCounts>(`/api/tasks/facet-counts?${searchParams.toString()}`);
}

export async function advanceIntake(taskId: string, section: SubmitSection) {
  const response = await fetchJSON(`/api/tasks/${taskId}/advance-intake`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(section),
  });

  return z.object({ success: z.literal(true) }).parse(response);
}

export async function advancePatientLookup(
  taskId: string,
  params: {
    lookupStep: PatientLookUpStep | null;
    patientId: string;
    ehrSystemId?: string | null;
    mrn?: string | null;
    ehrId?: string | null;
    secondaryEhrId?: string | null;
  }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/tasks/${taskId}/advance-patient-lookup`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function verifyAndComplete(
  taskId: string,
  params: {
    verificationResult: VerificationResult;
    reason?: string | null;
    details?: string | null;
  }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/tasks/${taskId}/verify-and-complete`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function resolveVisualDuplicate(
  taskId: string,
  params: { resolution: "duplicate" | "not_duplicate" }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/tasks/${taskId}/resolve-visual-duplicate`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function approveTask(
  taskId: string,
  params: { approved: boolean; notes?: string | null }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/tasks/${taskId}/approve`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function continueAfterManualTprStep(taskId: string, stepKey: string, bypass = false) {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/continue-after-manual-step`, {
    method: "POST",
    body: JSON.stringify({ stepKey, bypass }),
  });

  return manualStepContinuationResponseSchema.parse(response);
}

export async function overrideTprReceivingAssignment(
  taskId: string,
  payload: { providerNpi: string; targetDepartmentId: string }
): Promise<{ success: boolean; replayedStepKeys: string[]; providerEhrIdResolved: boolean }> {
  return fetchJSON<{ success: boolean; replayedStepKeys: string[]; providerEhrIdResolved: boolean }>(
    `/api/tasks/${taskId}/receiving-assignment`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

/** The task's derived step cards; `cards` is null when its customer has none for this workflow. */
export async function getTaskStepCards(taskId: string): Promise<StepCardsResponse> {
  return stepCardsResponseSchema.parse(await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/step-cards`));
}

/** Performs one action from a task's step card. */
export async function performStepCardAction(
  taskId: string,
  cardKey: string,
  action: StepCardActionRequest
): Promise<StepCardActionResponse> {
  return stepCardActionResponseSchema.parse(
    await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/step-cards/${encodeURIComponent(cardKey)}/actions`, {
      method: "POST",
      body: JSON.stringify(action),
    })
  );
}

export async function getTaskStepVerifications(taskId: string): Promise<TaskStepVerification[]> {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/step-verification`);

  return taskStepVerificationListSchema.parse(response).verifications;
}

export async function upsertTaskStepVerification(
  taskId: string,
  payload: UpsertTaskStepVerification
): Promise<TaskStepVerification> {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/step-verification`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return taskStepVerificationSchema.parse(response);
}

export async function getTaskApproval(taskId: string): Promise<TaskApproval | null> {
  try {
    return await fetchJSON<TaskApproval>(`/api/tasks/${taskId}/approval`);
  } catch (err) {
    if (isNotFound(err as Error)) {
      return null;
    }

    throw err;
  }
}

export async function getTaskHistory(taskId: string): Promise<TaskHistoryResponse> {
  return fetchJSON<TaskHistoryResponse>(`/api/tasks/${taskId}/history`);
}

export type AwaitingInformationResumeCheck = { resumable: boolean; reason?: string; blockedReason?: string };

export async function getWorkflowStepResumeCheck(taskId: string) {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/resume-workflow-step`);

  return workflowStepResumeCheckSchema.parse(response);
}

export async function resumeWorkflowStep(taskId: string, payload: WorkflowStepResumeRequest) {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/resume-workflow-step`, {
    method: "POST",
    body: JSON.stringify(workflowStepResumeRequestSchema.parse(payload)),
  });

  return workflowStepResumeResponseSchema.parse(response);
}

export async function getCanResumeAwaitingInformationTask(taskId: string): Promise<AwaitingInformationResumeCheck> {
  return fetchJSON<AwaitingInformationResumeCheck>(`/api/tasks/${taskId}/resume-awaiting-information`);
}

export async function resumeAwaitingInformationTask(
  taskId: string
): Promise<{ resumed: boolean; reason?: string; blockedReason?: string }> {
  return fetchJSON<{ resumed: boolean; reason?: string; blockedReason?: string }>(
    `/api/tasks/${taskId}/resume-awaiting-information`,
    {
      method: "POST",
    }
  );
}

export async function confirmInsuranceNetwork(taskId: string, scope: ConfirmInsuranceNetworkRequest) {
  const response = await fetchJSON<Record<string, unknown>>(`/api/tasks/${taskId}/confirm-insurance-network`, {
    method: "POST",
    body: JSON.stringify(confirmInsuranceNetworkRequestSchema.parse(scope)),
  });

  return confirmInsuranceNetworkResponseSchema.parse(response);
}
